import { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import { selectHotelData } from 'pages/redux';
import { selectLogs, selectBranchFiles } from 'pages/Logs/redux';
import { selectRooms } from 'pages/Hospitality/redux';
import { selectCustomers,
  selectRoomTypes,
  selectUsers,
  selectBilling,
} from 'pages/Settings/redux';
import { selectManagerReports } from 'pages/Reporting/redux';
import {
  countPhotoUsingItems,
  estimateBranchFilesBytes,
  estimateDbBytesFromCounts,
  estimatePhotoBytes,
  formatStorageBytes,
  hasStoragePlan,
  resolveDbStorageMaxBytes,
  resolveHotelStorageMaxBytes,
  resolveOthersStorageMaxBytes,
  resolveStorageWarnHeadroomBytes,
} from 'pages/Logs/logUtils';

export type HotelStorageSnapshot = {
  dbBytes: number,
  photoBytes: number,
  branchBytes: number,
  othersBytes: number,
  totalBytes: number,
  hotelMaxBytes: number,
  dbMaxBytes: number,
  othersMaxBytes: number,
  hasPlan: boolean,
  /** DB tone for right-pane alert (all DB entries). */
  dbTone: 'warn' | 'critical' | null,
  format: typeof formatStorageBytes,
};

/** Fast client-side hotel storage estimate from already-loaded Redux state. */
export const useHotelStorage = (): HotelStorageSnapshot => {
  const hotelData = useAppSelector(selectHotelData);
  const logs = useAppSelector(selectLogs);
  const customers = useAppSelector(selectCustomers);
  const rooms = useAppSelector(selectRooms);
  const users = useAppSelector(selectUsers);
  const roomTypes = useAppSelector(selectRoomTypes);
  const cards = useAppSelector(selectBilling);
  const branchFiles = useAppSelector(selectBranchFiles);
  const managerReports = useAppSelector(selectManagerReports);
  const inventory = hotelData && Array.isArray(hotelData.inventory) ? hotelData.inventory : [];

  return useMemo(() => {
    const photoCount = countPhotoUsingItems({ roomTypes, inventory, users });
    const feedbackCount = (customers || []).reduce((n, customer) => {
      const messages = customer?.feedback?.messages;
      return n + (Array.isArray(messages) ? messages.length : 0);
    }, 0);
    const dbBytes = estimateDbBytesFromCounts({
      logs: logs?.length || 0,
      customers: customers?.length || 0,
      inventory: inventory.length,
      rooms: rooms?.length || 0,
      staff: users?.length || 0,
      billing: cards?.length || 0,
      feedback: feedbackCount,
      config: 1,
      hotels: 1,
      photos: photoCount,
      managerReports: managerReports?.length || 0,
    });
    const photoBytes = estimatePhotoBytes(photoCount);
    const branchBytes = estimateBranchFilesBytes(branchFiles);
    const othersBytes = branchBytes + photoBytes;
    const totalBytes = dbBytes + othersBytes;
    const hasPlan = hasStoragePlan(hotelData);
    const hotelMaxBytes = resolveHotelStorageMaxBytes(hotelData);
    const dbMaxBytes = resolveDbStorageMaxBytes(hotelData);
    const othersMaxBytes = resolveOthersStorageMaxBytes(hotelData);
    const warnHeadroom = resolveStorageWarnHeadroomBytes(hotelData);

    let dbTone: 'warn' | 'critical' | null = null;
    if (dbBytes >= dbMaxBytes) dbTone = 'critical';
    else if (dbMaxBytes - dbBytes < warnHeadroom) dbTone = 'warn';

    return {
      dbBytes,
      photoBytes,
      branchBytes,
      othersBytes,
      totalBytes,
      hotelMaxBytes,
      dbMaxBytes,
      othersMaxBytes,
      hasPlan,
      dbTone,
      format: formatStorageBytes,
    };
  }, [
    hotelData,
    logs,
    customers,
    rooms,
    users,
    roomTypes,
    cards,
    branchFiles,
    managerReports,
  ]);
};

export default useHotelStorage;
